import { useConnectionEndpoint } from "../connectionAnalyser/useConnectionEndpoint";
import React, { lazy, useEffect, useRef, useState } from "react";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import { MoveHandler } from "./MoveHandler";
import Point from "../models/Point";
import Page, { mmToPixel } from "../models/Page";
import Consumer from "../models/Consumer";
import ConnectorGrab from "../components/ConnectorGrab";
import {
  onConnectionTargetMouseLeave,
  onConnectionTargetMouseMove,
  onConnectionTargetMouseUp,
} from "./ConnectionTarget";
import { ConnectionType, dehighlightConnections, highlightConnections } from "../models/Connection";
import { onKeyDown_ButtonOrActor } from "../components/DrawingArea";

import { showToast } from "../components/Toasts";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import Browser from "../browser";
import { useRedraw } from "../models/useRerender";
import { DragMode } from "../models/TreeviewModel";
import BaseModel from "../models/BaseModel";

import {
  bringElementToTop,
  getContrastTextColor,
  measureText,
  measureTextForPDF,
  pointInBounderyRect,
} from "./GraficElementUtils";
import ConnectableObject from "../models/ConnectableObject";
import Room from "../models/Room";

import EstadaSuspense from "../components/EstadaSuspense";

import { itx, noItx } from "../translationItx/itx";
import { RDiv } from "../components/renderPrimitives/RDiv";
import { RenderTarget, useRenderTarget } from "../components/renderPrimitives/renderTarget";
import { RImg } from "../components/renderPrimitives/RImg";
import { RText } from "../components/renderPrimitives/RText";
import Button from "../models/Button";
import ConsumerImages from "../models/ConsumerImages";
import ObjectReference from "../components/ObjectReference";
import { ConnectionDisplayModes } from "../State";
import ConsumerViewSvg from "./ConsumerViewSvg";
import ButtonViewSvg from "./ButtonViewSvg";
import { classicConsumerLayout } from "./classicPlanSvgLayout";

const logger = new Logger();

function LazyConsumerViewInstallExtension(props: { consumer: Consumer }) {
  return <EstadaSuspense Component={lazy(() => import("./ConsumerViewInstallExtension"))} props={props} />;
}

export function moveConnectableObjectTo(consumerRef: HTMLDivElement, x: number, y: number) {
  consumerRef.style.left = `${x}px`;
  consumerRef.style.top = `${y}px`;
}

//
// returns true if room is changed
//
export function handleRoomToRoomMove(mainPage: Page, consumerOrButton: ConnectableObject, pFloor: Point): boolean {
  if (mainPage.isFloorPlan) {
    const room = consumerOrButton.parentRoom;
    if (room) {
      //
      // Überprüfen ob der Conumer NICHT MEHR IM GLEICHEN RAUM liegt.
      // und nur dann schauen in welchen RAum er verschoben wird.
      //
      // Dadurch bleibt er im gleichen Raum,
      // wenn er in einem Bereich liegt wo sich 2 Räume überlappen
      //

      if (!pointInBounderyRect(room.boundingRectInFloorSpace, pFloor)) {
        if (pFloor && !pointInBounderyRect(room.boundingRectInFloorSpace, pFloor)) {
          const newRoom = mainPage.getRoomFromPointInBoundingReact(pFloor);
          if (newRoom) {
            consumerOrButton.moveToRoom(newRoom);
            state.selectionManager.selectedObject = undefined; // will focus naviagtion to the object in the treeview next line

            setTimeout(() => {
              state.selectionManager.selectedObject = consumerOrButton;
              //               state.selectionManager.scrollSelectedObjectIntoView()
            }, 500);
          }
          return true;
        }
      }
    }
  }
  return false;
}

export function forceShowIcons(mainPage: Page): boolean {
  let res = false;
  if (mainPage.isFloorPlan) {
    if (mainPage.currentZoom.value > 1) {
      res = true;
    }
  } else {
    if (mainPage.currentZoom.value > 1.5) {
      res = true;
    }
  }
  return res;
}

function ConnectionsToOtherRoomsIndicator(props: {
  consumerOrButton: Consumer | Button;
  thisElement: React.RefObject<HTMLDivElement>;
  forPdf: boolean;
}) {
  const { consumerOrButton, thisElement, forPdf } = props;

  if (
    state.connectionDisplayMode.value == ConnectionDisplayModes.none ||
    state.connectionDisplayMode.value == ConnectionDisplayModes.scenes ||
    state.connectionDisplayMode.value == ConnectionDisplayModes.channels
  ) {
    return null;
  }

  const partnersInOtherRooms = consumerOrButton.connectedObjectsInOtherRooms;
  if (partnersInOtherRooms.length == 0) {
    return null;
  }

  function onMouseMove(ev: any) {
    //
    // wenn die mouse über eine Consumer fährt,
    // bringe ihn ans den der List der ConsumerElemente,
    // so dass er in der Darstellung über den anderen liegt.
    //

    if (
      thisElement.current &&
      thisElement.current.parentNode &&
      thisElement.current !== thisElement.current.parentNode.lastElementChild
    ) {
      thisElement.current.parentNode.append(thisElement.current);
    }
  }

  return (
    <RDiv
      className="connectionsToOtherRoomsIndicator"
      style={{
        position: "absolute",
        bottom: "10px",
        left: "100%",
        flexDirection: "column",
        whiteSpace: "nowrap",
        alignItems: "left",
        minHeight: "20px",
        minWidth: "30px",
        pointerEvents: "auto",
      }}
      onMouseMove={onMouseMove}
    >
      <RDiv
        style={{
          position: "absolute",
          left: "0px",
          top: "0px",
          fontWeight: "bold",
          textAlign: "center",
          color: "blue",
          minWidth: "30px",
        }}
      >
        {
          "---" // no itx
        }
      </RDiv>

      {!forPdf && (
        <RDiv
          className="visibleOnHover"
          style={{
            position: "absolute",
            left: "25px",
            top: "-8px",

            borderRadius: "5px",
            padding: "2px",
            backgroundColor: "#aaa",
            flexDirection: "column",
          }}
        >
          {partnersInOtherRooms.map((partner) => (
            <ObjectReference
              key={partner.id}
              obj={partner}
              useAllwaysMaxSpace={true}
              showName={true}
              style={{
                cursor: "pointer",
                borderRadius: "20px",
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
              onUnlink={() => {
                const connection = consumerOrButton.getConnectionWith(partner);
                connection?.delete();
              }}
            />
          ))}
        </RDiv>
      )}
    </RDiv>
  );
}

export default function ConsumerView(props: {
  consumerOrButton: Consumer | Button;
  page: Page;
  mainPage: Page;
  print?: PagePrintInfo;
}) {
  const { consumerOrButton, page, mainPage, print } = props;

  const diagnosticMode = state.settings.diagnosticMode;
  const consumerSelected = consumerOrButton.selected && !diagnosticMode;

  const thisElement = useRef<HTMLDivElement>(null);
  // The positioning wrapper has zero size; measure the actual symbol bounds for connections.
  const connectionElement = useRef<HTMLDivElement>(null);
  const dragoverZoomElement = useRef<HTMLDivElement>(null);

  const isMoving = useRef<boolean>(false);
  const ignoreClick = useRef<boolean>(false);
  const inititalSelectionState = useRef(false);
  const redraw = useRedraw();
  const renderTarget = useRenderTarget();
  useConnectionEndpoint(state.connectionAnalyser, consumerOrButton, connectionElement, "drawing", !print && renderTarget === RenderTarget.web);

  const clickTimeout = useRef<any>(0);

  const initialPoint = useRef<Point | undefined>(undefined);

  const consumerRef = useRef(consumerOrButton);

  const xmoveHandler = useRef<MoveHandler | undefined>(undefined);

  async function init() {
    if (consumerOrButton.isButton) {
      //
      // Button
      //
      const button = consumerOrButton as Button;
      if (button.template) {
        if (!button.frontImage) {
          await button.template.knxProductData.loadFrontImage();
          redraw();
        }
      }
      imgSrc = consumerOrButton.getImgSrc();
    }

    if (imgSrc) {
      //
      // in pdf rendering: redraw is not working!
      // for pdf rendering loading is done beforehand in roomPlans data function
      //
      const status = await ConsumerImages.loadImageInfoAsync(imgSrc);
      if (status == "loaded") {
        redraw();
      }
    }
  }

  useEffect(() => {
    init();

    if (!print && !diagnosticMode) {
      xmoveHandler.current = initMoveHander();
      return () => {
        xmoveHandler.current?.release();
      };
    }
  }, []);

  // useOnChangeWatcher(mainPage.currentZoom, (changedProperty, changedObject) => {
  //   if (redrawTimer.current) {
  //     clearTimeout(redrawTimer.current);
  //   }
  //   redrawTimer.current = setTimeout(() => {
  //     redraw();
  //     redrawTimer.current = 0;
  //   }, 200);
  //   return false;
  // });

  useOnChangeWatcher(state.settings, (changedProperty) => {
    if (changedProperty == "diagnosticMode") {
      if (!state.settings.diagnosticMode) {
        if (!xmoveHandler.current) {
          xmoveHandler.current = initMoveHander();
        }
      } else {
        xmoveHandler.current?.release();
        xmoveHandler.current = undefined;
      }
    }

    return true;
  });

  useOnChangeWatcher(consumerOrButton, (changedProperty, changedObject) => {
    if (changedProperty == "selected") {
      if (consumerOrButton.selected) {
        bringElementToTop(thisElement.current);
        highlightConnections(true, page, consumerOrButton);
      } else {
        dehighlightConnections(true, page, consumerOrButton);
      }
    }
    return true;
  });
  useOnChangeWatcher(state.currentEditObject);

  // Runs after every render: if the current imgSrc is not yet in ConsumerImages,
  // load it asynchronously and redraw. This covers cases where the template changes
  // after mount (e.g. catalog assignment) without remounting the component.
  useEffect(() => {
    let src: string | undefined = undefined;
    if (state.settings.showIcons) {
      src = consumerOrButton.getImgSrc();
    }
    if (src && !ConsumerImages.getImageInfoSync(src)) {
      ConsumerImages.loadImageInfoAsync(src).then((status) => {
        if (status === "loaded") redraw(); // no itx
      });
    }
  });

  if (consumerOrButton.isDeleted) {
    return null;
  }

  logger.log(`render consumer ${consumerOrButton.tag}`);

  function clearFocus() {
    if (document?.activeElement) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }

  function initMoveHander(): MoveHandler | undefined {
    if (!print && !diagnosticMode) {
      var xmoveHandler = new MoveHandler(
        thisElement,
        "#attachMoveHandler", // no itx
        mainPage.isFloorPlan ? (consumerOrButton.parent as Room) : undefined,
        mainPage,
      );

      xmoveHandler.onMouseDown = () => {
        inititalSelectionState.current = consumerSelected;
        ignoreClick.current = false;
        // initialPoint.current = consumerRef.current.position.clone() // error in debug clone() is not a function
        initialPoint.current = new Point(consumerRef.current.position.x, consumerRef.current.position.y);
        logger.log(`set initial point ${initialPoint.current.x} ${initialPoint.current.y}`);
      };

      xmoveHandler.onCancel = () => {
        ignoreClick.current = true;
        isMoving.current = false;
        if (initialPoint.current) {
          logger.log(`restore initial point ${initialPoint.current.x} ${initialPoint.current.y}`);

          consumerRef.current.position = initialPoint.current.clone();

          logger.log(`restore initial point ${consumerRef.current.position.x} ${consumerRef.current.position.y}`);
          setTimeout(() => {
            logger.log(`restore initial point A ${consumerRef.current.position.x} ${consumerRef.current.position.y}`);
          }, 1000);
        }
        redraw();

        clearFocus();
        setTimeout(() => {
          if (state.selectionManager.selectedObject == consumerOrButton) {
            state.selectionManager.selectedObject = undefined;
          }
        }, 100);
      };

      xmoveHandler.onMouseUp = (ev) => {
        // if (!ignoreClick.current) {
        //   showProperties();
        // }
        // dont do it, runs before onClick() ....:
        //  ignoreClick.current = false;
      };

      xmoveHandler.onDblClick = (ev) => {
        ev.stopPropagation();
        if (clickTimeout.current) {
          clearTimeout(clickTimeout.current);
          clickTimeout.current = 0;
        }

        if (!ignoreClick.current) {
          state.currentEditObject.value = undefined;
          state.selectionManager.selectedObject = consumerOrButton;
          consumerOrButton.treeViewModel.handleOpen();
        }

        ignoreClick.current = false;
        isMoving.current = false;
      };

      xmoveHandler.onClick = (ev: React.MouseEvent<HTMLDivElement>) => {
        if (!diagnosticMode && !ignoreClick.current) {
          ev.stopPropagation();
          showProperties();
          isMoving.current = false;
        }
      };

      xmoveHandler.onMoveStarted = () => {
        isMoving.current = true;
        ignoreClick.current = true;
      };

      xmoveHandler.onMoveInfo = () => {
        consumerOrButton.updateConnectionEndpoints();
      };

      xmoveHandler.onMoveFinished = (pLocal: Point, pFloor: Point) => {
        if (isMoving.current) {
          consumerRef.current.position = pLocal;
          handleRoomToRoomMove(mainPage, consumerOrButton, pFloor);

          page.markAsDirty();
          consumerRef.current.updateConnectionEndpoints();
          isMoving.current = false;
        }
      };

      return xmoveHandler;
    }
  }

  const position = consumerOrButton.position;

  let selectFrameClassName = "selectFrame"; // no itx need to has consumer in because of get throu to other divs(name, tag ...)
  if (consumerSelected) {
    selectFrameClassName += " selected"; // no itx
  }

  let tooltipText: string | undefined;
  if (consumerOrButton.isConsumer) {
    const connectedToChannel = consumerOrButton.getConnectedChannel() != undefined;
    if (!connectedToChannel) {
      selectFrameClassName += " noChannelConnection"; // no itx
      tooltipText = itx(
        "consumerView.consumerNotConnectedWithChannel",
        "Der Verbraucher ist mit keinem Kanal verbunden",
      );
    }
  }

  function QuantityShowage(props: {}) {
    if (!consumerOrButton.isConsumer) return null;

    const consumer = consumerOrButton as Consumer;
    if (consumer.quantity != 1) {
      // from showage >=100%
      return (
        <RDiv
          style={{ width: "100%", position: "absolute", left: "20px", top: "15px", zIndex: 1 }}
          id={"quantityShowage" + consumerOrButton.id} // no itx
        >
          <RDiv
            data-noitx="true" // no itx
            style={{
              border: "1px solid #aaa",
              position: "absolute",
              left: "0px",
              bottom: "0px",
              borderRadius: "50px",
              width: `${fontSize * 1.5}px`,
              height: `${fontSize * 1.5}px`,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: `${fontSize}px`,
              backgroundColor: "white",
            }}
          >
            x{consumer.quantity}
          </RDiv>
        </RDiv>
      );
    }
    return null;
  }

  function onMouseMove(ev: any) {
    if (!print && ev.buttons == 1 && !isMoving.current && thisElement.current) {
      onConnectionTargetMouseMove(page, consumerOrButton, thisElement.current, ConnectionType.FromSourceDevice);
    }

    //
    // wenn die mouse über eine Consumer fährt,
    // bringe ihn ans den der List der ConsumerElemente,
    // so dass er in der Darstellung über den anderen liegt.
    //

    if (
      thisElement.current &&
      thisElement.current.parentNode &&
      thisElement.current !== thisElement.current.parentNode.lastElementChild
    ) {
      thisElement.current.parentNode.append(thisElement.current);
    }
  }

  function onMouseUp() {
    if (!print && !isMoving.current && thisElement.current) {
      let connectionTarget = ConnectionType.FromSourceDevice;

      onConnectionTargetMouseUp(consumerOrButton, thisElement.current, ConnectionType.FromSourceDevice);
    }
  }

  function onClickGrab() {
    //
    // deactiviet bis die Funktion wirklick implementiert ist
    // mwe 25.11.2024
    //
    // state.dialogManager.openDialog(<ConnectDialog page={page} consumer={consumerOrButton} />);
  }

  const touchSelected = consumerSelected && Browser.isTouch() ? "touchSelected" : ""; // no itx

  async function handleDragEnter(mode: DragMode, sData: string) {
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          switch (obj.className) {
            case noItx("Fuse"):
            case noItx("Terminal"):
              dragoverZoomElement.current?.classList.add("zoomOnDragOver"); // no itx
              break;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }
  async function handleDrop(mode: DragMode, sData: string) {
    let txt = state.translate("treeViewModel.dragADropforbidden", `Drag&Drop ist nicht erlaubt`);
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          const res = await consumerOrButton.handleDrop2(mode, obj);
          if (res.errorMessage) {
            txt += ":\n" + res.errorMessage;
          }
          if (res.success) {
            if (res.errorMessage) {
              showToast({
                isError: false,
                text: res.errorMessage,
                time: 5000,
              });
            }
            return;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }

    showToast({
      isError: true,
      text: txt,
    });
  }

  function onDragEnter(e: any) {
    try {
      if (state.dragData) {
        const mode = e.dataTransfer.effectAllowed;
        handleDragEnter(mode, state.dragData);
        return;
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  function onDragLeave(e: any) {
    dragoverZoomElement.current?.classList.remove("zoomOnDragOver"); // no itx
  }

  function onDrop(e: any) {
    try {
      var items = e.dataTransfer.items;
      const item = items[0];
      if (item.kind === "string") {
        item.getAsString(function (sData: string) {
          const mode = e.dataTransfer.effectAllowed;
          handleDrop(mode, sData);
        });
      }

      dragoverZoomElement.current?.classList.remove("zoomOnDragOver"); // no itx
    } catch (ex) {
      logger.error(ex);
    }

    e.preventDefault();
  }
  function onContextMenu(ev: any) {
    state.contextMenuManager.show(ev, consumerOrButton);
  }

  function showProperties() {
    //
    // use timeout to avoid disturbing of double click
    //
    if (!clickTimeout.current) {
      clickTimeout.current = setTimeout(() => {
        clickTimeout.current = 0;
        if (!ignoreClick.current) {
          state.selectionManager.selectedObject = consumerOrButton;
          const p = consumerOrButton.getScreenPosition(page);
          state.currentEditObjectPosition.value = p;
          state.currentEditObject.value = consumerOrButton;
          // has to be className because of sensor is the same class but not isButton
          if (consumerOrButton.isButton) {
            state.showButtonChannels.value = { button: consumerOrButton as Button };
          }

          page.resetIsNewOnAllConnections();
          ignoreClick.current = false;
        }
      }, 300);
    }
  }

  let backgroundColor = consumerOrButton.drawingItemColor();

  let tag = state.settings.showTags ? consumerOrButton.tag : undefined;

  const forPdf = renderTarget === RenderTarget.pdf;
  const SymbolSvg = consumerOrButton.isConsumer ? ConsumerViewSvg : ButtonViewSvg;
  const svgLayout = classicConsumerLayout({
    size: state.settings.drawingItemSize * (consumerOrButton.selected ? 1.2 : 1), pixelsPerMm: mmToPixel,
    floorPlan: mainPage.isFloorPlan,
    roomScale: consumerOrButton.parentRoom?.isFloorGeneralRoom ? 1 : consumerOrButton.parentRoom?.roomtoFloorDrawingTransform?.scale || 1,
    consumer: consumerOrButton.isConsumer, tag,
    image: state.settings.showIcons ? consumerOrButton.getImgSrc() : undefined,
    background: backgroundColor, color: getContrastTextColor(backgroundColor),
    quantity: consumerOrButton.isConsumer ? (consumerOrButton as Consumer).quantity : undefined,
  }, measureText);

  let size = state.settings.drawingItemSize;

  if (consumerOrButton.selected) {
    size *= 1.2;
  }

  if (mainPage.isFloorPlan) {
    const room = consumerOrButton.parentRoom;
    if (!room?.isFloorGeneralRoom) {
      size = size / (room?.roomtoFloorDrawingTransform?.scale || 1);
    }
    size = size * 0.5;
  }

  let width = mmToPixel * size;
  let height = mmToPixel * size;
  let borderRadius = 0;
  let fontSize = height * 0.5;
  fontSize = Math.round(fontSize * 10) / 10;

  if (consumerOrButton.isConsumer) {
    borderRadius = width / 2;
  } else {
    borderRadius = 2;
  }

  const imgHeight = tag ? height * 0.5 : height - 10;

  let imgSrc: string | undefined = undefined;
  if (state.settings.showIcons) {
    imgSrc = consumerOrButton.getImgSrc();
  }

  let imageHeight = height;
  if (consumerOrButton.isButton) {
    const button = consumerOrButton as Button;
    if (button.template) {
      imageHeight = height - 7;
    }
  }

  // imageLeft is always based on imgHeight – stable regardless of aspect ratio.
  const imageLeft = (width - imgHeight) / 2;
  const imgageWidth = imgHeight; // square container; objectFit:contain handles aspect ratio

  let imgInfoIsMissing = true;
  if (imgSrc) {
    if (ConsumerImages.getImageInfoSync(imgSrc)) {
      imgInfoIsMissing = false;
    }
  }

  let imgStyle: any = {
    position: "relative",
    height: imgHeight + "px",
    width: imgHeight + "px",
    objectFit: "contain" as const, // to maintain aspect ratio and fit within the square container ????// no itx
  };
  if (forPdf) {
    imgStyle = {
      position: "relative",
      height: "auto",
      maxHeight: imgHeight + "px",
      width: "100%",
    };
  }

  const imgWrapperStyle: any = forPdf
    ? {
        // PDF: no absolute positioning — consumerShape's flex (alignItems: center) centers the image
        width: imgHeight + "px",
        height: imgHeight + "px",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      }
    : {
        position: "absolute",

        // MWE 7.9.2025
        //
        // rein symtomatisch fix für pdf,
        // ich habe im Moment keine Ahnung wo der versatz herkommt.
        //
        left: imageLeft + "px",
        //

        top: width / 6 + "px",
        width: imgHeight + "px", // picture should be quadratical ?.
        height: height + "px",
        display: "flex",
        justifyContent: "left",
        alignItems: "top",
      };

  let optimizeImage = true;

  let tagStyle: any = imgSrc
    ? {
        position: "absolute",
        bottom: consumerOrButton.isConsumer ? "5px" : "0px", // no itx
        width: width + "px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }
    : {};

  let styleOuter: any = {
    position: "absolute",
    pointerEvents: diagnosticMode ? "none" : undefined,
    cursor: "default",
    zIndex: consumerSelected ? 10 : undefined,
    // visibility: consumerOrButton.selected ? "hidden" : undefined,
  };

  if (tag && imgSrc) {
    height = height * 1.5;
  }

  styleOuter = forPdf
    ? {
        ...styleOuter,
        left: position.x - width / 2 + "px",
        top: position.y - height / 2 + "px",
      }
    : {
        ...styleOuter,
        left: position.x + "px",
        top: position.y + "px",
      };

  const styleInner: any = {
    position: "absolute",
    left: "0px",
    top: "0px",
    width: width + "px",
    height: height + "px",
  };

  if (!forPdf) {
    styleInner.left = -width / 2 + "px";
    styleInner.top = -height / 2 + "px";
  }

  let textColor = getContrastTextColor(backgroundColor);

  if (imgSrc && textColor == "#fff") {
    if (imgSrc.toLowerCase().endsWith(".png")) {
      imgSrc = imgSrc.substring(0, imgSrc.length - 4) + "-white" + imgSrc.substring(imgSrc.length - 4); // no itx
    }
  }

  if (tag) {
    const measureFn = forPdf ? measureTextForPDF : measureText;
    const measuredWidth = measureFn(tag, fontSize + "px").width;

    const w1 = width - 10; // 2 pixel padding vorne und hinten
    if (measuredWidth > w1) {
      fontSize *= w1 / measuredWidth;
      fontSize = Math.round(fontSize * 10) / 10;
    }
  }

  return (
    <RDiv id={consumerOrButton.id} style={styleOuter} ref={thisElement}>
      <RDiv style={styleInner} ref={connectionElement}>
        {!mainPage.isFloorPlan && (
          <ConnectionsToOtherRoomsIndicator
            consumerOrButton={consumerOrButton}
            thisElement={thisElement}
            forPdf={forPdf}
          />
        )}

        <RDiv
          id="attachMoveHandler"
          className={`consumer connectionTarget ${touchSelected} ${consumerSelected ? "selected" : ""}`}
          style={{
            cursor: print || diagnosticMode ? "auto" : "move",
            pointerEvents: "none",
            transformOrigin: `${width / 2}px ${height / 2}px`,
          }}
          data-tooltip-id="tv-tooltip"
          data-tv-toast=""
          data-tooltip-content={tooltipText}
          data-tooltip-place="top" // no itx
          data-tooltip-variant="warning" // no itx
          onContextMenu={onContextMenu}
          onDrop={onDrop}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onMouseEnter={(ev) => {
            if (print == undefined && ev.buttons == 0) {
              highlightConnections(true, page, consumerOrButton);
              if (diagnosticMode) {
                bringElementToTop(thisElement.current);
              }
            }
          }}
          onMouseLeave={(ev) => {
            console.log("consumer on mouse leave");
            if (!consumerSelected) {
              if (print == undefined && ev.buttons == 0) {
                dehighlightConnections(true, page, consumerOrButton);
              }
            }
            if (print == undefined && ev.buttons == 1 && thisElement.current) {
              onConnectionTargetMouseLeave(thisElement.current);
            }
          }}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
        >
          {/* ----  guideTarget -------------------------------------------------------- */}
          {!forPdf && (
            <RDiv
              className="selectBorder guideTarget"
              style={{
                position: "absolute",
                left: "-10px",
                top: "-10px",
                width: "calc(100% + 20px)",
                height: "calc(100% + 20px)",
                // pointerEvents: "auto",
              }}
            />
          )}

          {/* ---   selectFrame --------------------------------------------------------- */}

          <RDiv
            className={selectFrameClassName}
            ref={dragoverZoomElement}
            style={{
              position: "absolute",
              left: "0px",
              top: "0px",
              width: width + "px",
              height: height + "px",

              zIndex: consumerSelected ? "3" : undefined,
              pointerEvents: "none",
            }}
            tabIndex={1}
            onKeyDown={(ev) => {
              if (!print) {
                // Enter beendet den aktuellen Edit-/Selektionszustand dieses Consumers.
                if (ev.key === "Enter" || ev.keyCode === 13) {
                  // ggf. laufenden Klick-Timer abbrechen, damit keine späte Re-Selektion passiert
                  if (clickTimeout.current) {
                    clearTimeout(clickTimeout.current);
                    clickTimeout.current = 0;
                  }
                  // Edit-Overlay schließen und globale Auswahl zurücksetzen
                  state.currentEditObject.value = undefined;
                  state.selectionManager.selectedObject = undefined;
                  // Fallback: falls selected-Flag noch gesetzt ist, explizit entfernen
                  if (consumerOrButton.selected) {
                    consumerOrButton.selected = false;
                    consumerOrButton.raiseOnChange("selected");
                  }
                  // Verbindungs-Highlight entfernen und Fokus lösen (visuell "nicht ausgewählt")
                  dehighlightConnections(true, page, consumerOrButton);
                  clearFocus();
                  ev.preventDefault();
                  ev.stopPropagation();
                  return;
                }
                // Standard-Tastatursteuerung (z. B. Pfeiltasten bewegen Objekt)
                onKeyDown_ButtonOrActor(ev);
              }
            }}
          >
            {!diagnosticMode && (
              <ConnectorGrab
                mainPage={mainPage}
                page={page}
                connectableObject={consumerOrButton}
                x={0}
                portName="L"
                direction={180}
                // size={0.5}
                type={ConnectionType.FromSourceDevice}
                ownerSelected={consumerSelected}
                disabled={print != undefined}
                onClick={onClickGrab}
              />
            )}

            {!diagnosticMode && (
              <ConnectorGrab
                mainPage={mainPage}
                page={page}
                connectableObject={consumerOrButton}
                x={100}
                type={ConnectionType.FromSourceDevice}
                portName="R"
                // size={0.5}
                ownerSelected={consumerSelected}
                disabled={print != undefined}
                onClick={onClickGrab}
              />
            )}

            {forPdf && <QuantityShowage />}
            {state.currentEditObject.value != consumerOrButton && (!forPdf ? (
              <div className="consumerShape" style={{ position: "absolute", left: 0, top: 0,
                width, height, padding: 0, overflow: "visible", borderRadius, pointerEvents: "auto" }}>
                <SymbolSvg {...svgLayout} />
              </div>
            ) : (
              <RDiv
                className="consumerShape"
                style={{
                  position: "absolute",
                  left: "0px",
                  top: "0px",

                  height: height + "px",
                  width: width + "px",
                  borderRadius: `${borderRadius}px`,

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",

                  // border: consumerOrButton.selected ? "3px solid orange" : undefined,

                  backgroundColor: backgroundColor,

                  paddingBottom: consumerOrButton.isConsumer && tag ? "10px" : undefined,
                  paddingTop: consumerOrButton.isConsumer && tag ? "10px" : undefined,

                  pointerEvents: "auto",
                }}
              >
                {imgSrc && (
                  <RDiv style={imgWrapperStyle}>
                    <RImg
                      key={imgInfoIsMissing ? "noImg" : "img"}
                      style={imgStyle}
                      src={imgSrc}
                      noOptimize={!optimizeImage}
                      // autoWidth={true}
                      draggable={false}
                    />
                  </RDiv>
                )}

                {tag && (
                  <RDiv style={tagStyle}>
                    <RText style={{ fontSize: fontSize + "px", color: textColor }}>{tag}</RText>
                  </RDiv>
                )}
              </RDiv>
            ))}
          </RDiv>

          {diagnosticMode && consumerOrButton.isConsumer && (
            <LazyConsumerViewInstallExtension consumer={consumerOrButton as Consumer} />
          )}
        </RDiv>
      </RDiv>
    </RDiv>
  );
}
